// The connected wallet's account, live: the relayer's public account stream (server-sent
// events, through the page's proxy), each frame folded by the SDK with the venue's own checks.
// A frame out of sync, a dropped stream, or one quiet past 35 s (its heartbeat is every 10 s) is
// opened again after 1 s doubling to 30 s; a fresh stream starts with a snapshot.

import { pt, isPapertradeError, type Account, type Closed, type Hex, type Outcome } from "../sdk";
import { createStore, useStore } from "./store";

export type StreamStatus = "off" | "connecting" | "live" | "reconnecting";

export type AccountState = {
  owner: string | null;
  account: Account | null;
  status: StreamStatus;
  /** Closes and liquidations since the stream opened, newest first. */
  closed: (Closed & { at: number })[];
  /** How intents ended since the stream opened, newest first. */
  outcomes: { id: Hex; outcome: Outcome; at: number }[];
};

const KEPT = 50;
const QUIET_MS = 35_000;

export const accountState = createStore<AccountState>({
  owner: null,
  account: null,
  status: "off",
  closed: [],
  outcomes: [],
});

export const useAccountState = () => useStore(accountState);
export const useAccount = () => useStore(accountState, (state) => state.account);
export const useStreamStatus = () => useStore(accountState, (state) => state.status);
export const useOwner = () => useStore(accountState, (state) => state.owner);

type Events = { closed: Closed[]; outcomes: [Hex, Outcome][] };
const listeners = new Set<(events: Events) => void>();

/** Closes, liquidations and outcomes as each patch reports them. */
export function onAccountEvents(listener: (events: Events) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

let source: EventSource | null = null;
let watch: ReturnType<typeof setInterval> | null = null;
let retry: ReturnType<typeof setTimeout> | null = null;
let heard = 0;
let attempts = 0;
let following: string | null = null;

function patch(next: Partial<AccountState>) {
  accountState.set((state) => ({ ...state, ...next }));
}

function take(owner: string, data: string): boolean {
  try {
    const account = pt.observe(owner, data);
    if (account.applied === "stale") return true;
    const now = Date.now();
    accountState.set((state) => ({
      ...state,
      account,
      status: "live",
      closed: account.closed.length
        ? [...account.closed.map((closed) => ({ ...closed, at: now })), ...state.closed].slice(0, KEPT)
        : state.closed,
      outcomes: account.outcomes.length
        ? [...account.outcomes.map(([id, outcome]) => ({ id, outcome, at: now })), ...state.outcomes].slice(0, KEPT)
        : state.outcomes,
    }));
    if (account.closed.length || account.outcomes.length) {
      listeners.forEach((listener) => listener({ closed: account.closed, outcomes: account.outcomes }));
    }
    return true;
  } catch (error) {
    if (isPapertradeError(error) && error.kind === "outOfSync") return false;
    console.warn("account frame", error);
    return false;
  }
}

function open(owner: string) {
  if (following !== owner) return;
  patch({ status: attempts === 0 ? "connecting" : "reconnecting" });
  const stream = new EventSource(pt.streamUrl(owner));
  source = stream;
  heard = Date.now();
  const alive = () => {
    heard = Date.now();
  };
  stream.addEventListener("open", alive);
  stream.addEventListener("liveness", alive);
  stream.addEventListener("heartbeat", alive);
  stream.addEventListener("wallet", (event) => {
    heard = Date.now();
    if (take(owner, (event as MessageEvent<string>).data)) attempts = 0;
    else reopen(owner);
  });
  stream.addEventListener("error", () => {
    if (stream.readyState === EventSource.CLOSED) reopen(owner);
    else patch({ status: "reconnecting" });
  });
}

function close() {
  source?.close();
  source = null;
}

function reopen(owner: string) {
  close();
  pt.forget(owner);
  later(owner);
}

function later(owner: string) {
  if (retry) return;
  const wait = Math.min(30_000, 1_000 * 2 ** Math.min(attempts, 5));
  attempts += 1;
  patch({ status: "reconnecting" });
  retry = setTimeout(() => {
    retry = null;
    open(owner);
  }, wait);
}

/** Follows `owner`'s account (null stops). Following the same owner again keeps the stream. */
export function follow(owner: string | null) {
  const next = owner?.toLowerCase() ?? null;
  if (next === following) return;
  if (following) {
    close();
    pt.forget(following);
  }
  if (retry) clearTimeout(retry);
  retry = null;
  attempts = 0;
  following = next;
  accountState.set({ owner: next, account: null, status: next ? "connecting" : "off", closed: [], outcomes: [] });
  if (!next) {
    if (watch) clearInterval(watch);
    watch = null;
    return;
  }
  watch ??= setInterval(() => {
    if (following && source && Date.now() - heard > QUIET_MS) reopen(following);
  }, 5_000);
  open(next);
}

/** The account re-read against the latest trading state (claimable rewards move with it). */
export function refreshAccount() {
  const owner = accountState.get().owner;
  if (!owner) return;
  const account = pt.account(owner);
  if (account) patch({ account });
}
